import React, {useEffect, useMemo, useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, message as messageApi, Table} from 'antd';
import CentralLayout from "../../../layouts/CentralLayout";
import FilterArea, { DEFAULT_CARRIER_FILTER_OPTIONS, type CarrierFilterOptions } from './components/FilterArea';
import CarrierFormDrawer from './components/CarrierFormDrawer';
import {GET_ALL_CARRIERS, useAllCarriers} from 'pages/central/carrier/graphql/queries/getAllCarriers';
import {useSaveCarrier} from './graphql/mutations/saveCarrier';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';
import {showError} from 'utils/exceptionUtils';
import Loading from "../../../components/Loading";
import { useTranslation } from 'react-i18next';
import {CarrierType} from "../../../graphql/types/CarrierType";
import {formatTimestampToDateTime} from "../../../utils/dateTimeUtils";
import {SiteType} from "../../../graphql/types/SiteType";
import TablePaginationBar from '../../../components/TablePaginationBar';
import { ALL_CENTER_WAREHOUSE_ID } from '../../../enums/TmsEnum';

const PageBoy: React.FC = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filteredData, setFilteredData] = useState<CarrierType[]>([]);
    const [filters, setFilters] = useState<CarrierFilterOptions>(DEFAULT_CARRIER_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {t} = useTranslation();
    // GraphQL hooks
    const {allCarriers, loading, error, refetch} = useAllCarriers();
    const {warehouseOptions, loading: warehousesLoading} = useWarehouseOptions(SiteType.HUB);

    // Extend warehouse options: add Nationwide as the first option
    const extendedWarehouseOptions = useMemo(() => {
        const nationwide = { id: ALL_CENTER_WAREHOUSE_ID, name: t('common.allCenterWarehouse') };
        // Avoid duplicating if already present
        const exists = warehouseOptions.some(w => String(w.id) === ALL_CENTER_WAREHOUSE_ID);
        return exists ? warehouseOptions : [nationwide, ...warehouseOptions];
    }, [warehouseOptions, t]);

    const [saveCarrier, {loading: savingCarrier}] = useSaveCarrier();

    // Initialize filtered data when carrierData changes
    useEffect(() => {
        setFilteredData(allCarriers);
        setFilters(DEFAULT_CARRIER_FILTER_OPTIONS);
        setCurrentPage(1);
    }, [allCarriers]);

    // Handle GraphQL errors
    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error]);

    const pagedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return filteredData.slice(start, end);
    }, [filteredData, currentPage, pageSize]);

    const handleFilter = () => {
        let result = [...allCarriers];

        if (filters.carrierName) {
            result = result.filter(c => c.carrierName?.includes(filters.carrierName));
        }

        if (filters.contactName) {
            result = result.filter(c => c.contactName?.includes(filters.contactName));
        }

        if (filters.carrierType !== null) {
            result = result.filter(c => c.carrierType === filters.carrierType);
        }

        if (filters.status !== null) {
            result = result.filter(c => c.status === filters.status);
        }

        setFilteredData(result);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(DEFAULT_CARRIER_FILTER_OPTIONS);
        setFilteredData(allCarriers);
        setCurrentPage(1);
    };

    // Show new Carrier drawer
    const showNewCarrierDrawer = () => {
        setSelectedCarrier(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show Carrier details
    const showCarrierDetail = (carrier: CarrierType) => {
        setSelectedCarrier(carrier);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle Carrier form submission (create or update)
    const handleCarrierSubmit = async (values: CarrierType) => {
        try {
            const carrierInput = {
                id: values.id || undefined,
                carrierCode: values.carrierCode,
                carrierName: values.carrierName,
                contactName: values.contactName || undefined,
                contactPhone: values.contactPhone || undefined,
                carrierType: values.carrierType || undefined,
                status: values.status,
                centerWarehouseIds: values.centerWarehouseIds
            };

            await saveCarrier({
                variables: {input: carrierInput},
                refetchQueries: [{query: GET_ALL_CARRIERS}]
            });

            if (drawerMode === 'create') {
                messageApi.success(t('central.carrier.messages.carrierCreatedSuccessfully') as string);
            } else {
                messageApi.success(t('central.carrier.messages.carrierUpdatedSuccessfully') as string);
            }

            setDrawerVisible(false);
            // 重新获取数据
            refetch();
        } catch (error: any) {
            showError(error);
        }
    };

    // Table column definitions
    const carrierColumns: TableColumnsType<CarrierType> = [
        {
            title: t('central.carrier.pageDetails.carrierId'),
            dataIndex: 'carrierCode',
            key: 'carrierCode',
            align: 'left' as const,
            ellipsis: false,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showCarrierDetail(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                </Button>
            ),
        },
        {
            title: t('central.carrier.pageDetails.companyName'),
            dataIndex: 'carrierName',
            key: 'carrierName',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.carrier.pageDetails.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.carrier.pageDetails.phone') ,
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.carrier.pageDetails.acceptedRouteType'),
            dataIndex: 'carrierType',
            key: 'carrierType',
            align: 'left' as const,
            ellipsis: false,
            render: (value) => {
                let text = '-';
                if (value === 1) text = t('central.carrier.types.trunk');
                else if (value === 2) text = t('central.carrier.types.branch');
                else if (value === 3) text = t('central.carrier.types.trunkAndBranch');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.carrier.pageDetails.warehouse'),
            dataIndex: 'centerWarehouseIds',
            key: 'centerWarehouseIds',
            align: 'left' as const,
            ellipsis: false,
            render: (centerWarehouseIds) => {
                // 处理数组格式或多选格式的仓库ID
                const warehouseIds = Array.isArray(centerWarehouseIds) 
                    ? centerWarehouseIds 
                    : centerWarehouseIds != null 
                        ? [centerWarehouseIds] 
                        : [];
                
                if (warehouseIds.length === 0) {
                    return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                }
                
                const warehouseNames = warehouseIds
                    .map(id => {
                        const wh = extendedWarehouseOptions.find(w => String(w.id) === String(id));
                        return wh?.name;
                    })
                    .filter(Boolean);
                
                const text = warehouseNames.length > 0 ? warehouseNames.join(', ') : '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.carrier.pageDetails.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (value) => {
                const text = value === 1 ? t('enum.activeStatus.active') : (value === 0 ? t('enum.activeStatus.suspended') : '-');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.carrier.pageDetails.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            align: 'left' as const,
            ellipsis: false,
            render: (updatedAt: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{updatedAt ? formatTimestampToDateTime(updatedAt) : '--'}</span>
            ),
        },
        {
            title: t('components.button.action'),
            key: 'action',
            width: 100,
            align: 'left' as const,
            render: (_: any, record: CarrierType) => (
                <Button type="link" onClick={() => showCarrierDetail(record)} style={{ paddingLeft: 0 }}>
                    {t('components.button.edit')}
                </Button>
            ),
        },
    ];

    if (loading) {
        return (
            <Loading spinning={loading}/>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            <FilterArea
                warehousesOptions={extendedWarehouseOptions}
                loading={warehousesLoading}
                filters={filters}
                setFilters={setFilters}
            />

            {/* Action buttons */}
            <div className="flex justify-end gap-2 mb-3">
                <Button
                    type="primary"
                    onClick={showNewCarrierDrawer}
                    loading={savingCarrier}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.new')}</span>
                </Button>
                <Button
                    type="primary"
                    onClick={handleFilter}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.filter')}</span>
                </Button>
                <Button
                    onClick={handleResetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.reset')}</span>
                </Button>
            </div>

            {/* carrier table */}
            <Table
                rowKey="id"
                columns={carrierColumns}
                dataSource={pagedData}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
                className="mb-4"
            />

            <TablePaginationBar
                total={filteredData.length}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) {
                        setPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                onShowSizeChange={(_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* carrier form drawer (shared for create/edit) */}
            <CarrierFormDrawer
                warehousesOptions={extendedWarehouseOptions}
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleCarrierSubmit}
                carrier={selectedCarrier}
                mode={drawerMode}
            />
        </div>
    );
};

const CarrierPage: React.FC = () => {
    return (
        <PageBoy/>
    );
};

export default CarrierPage;